import { Input, Brand, Button, ErrorNotice, Field, Modal } from '@kiipu/ui';
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, CheckCircle, Info, WarningCircle } from '@phosphor-icons/react';

import { useTranslation } from 'react-i18next';
import { api, request } from '../api';
import { sessionKey, useSession } from '../context';
import { SocialSignInButtons } from '../components/integrations';
import { EmailChip, PasswordInput } from '../components/sign-in-fields';
import { isDesktop } from '../lib/client-platform';
import { DesktopAuthPage } from '../components/desktop-auth';
import '../styles/auth.css';

type Turnstile = {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
      theme: string;
    },
  ) => string;
  remove: (id: string) => void;
};
declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}
function Challenge({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let widget: string | undefined;
    let cancelled = false;
    const render = () => {
      if (!cancelled && window.turnstile && ref.current && !widget)
        widget = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action: 'auth_email',
          callback: onToken,
          'expired-callback': () => onToken(''),
          'error-callback': () => {
            onToken('');
            setError(true);
          },
          theme: 'auto',
        });
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
    const script = existing || document.createElement('script');
    if (!existing) {
      script.dataset.turnstile = 'true';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.append(script);
    }
    script.addEventListener('load', render);
    const failed = () => setError(true);
    script.addEventListener('error', failed);
    render();
    return () => {
      cancelled = true;
      script.removeEventListener('load', render);
      script.removeEventListener('error', failed);
      if (widget) window.turnstile?.remove(widget);
    };
  }, [siteKey, onToken]);
  return (
    <div>
      <div ref={ref} />
      {error ? <ErrorNotice error="验证暂不可用，请刷新后重试。" /> : null}
    </div>
  );
}
function PasswordRecovery({ siteKey, defaultEmail }: { siteKey?: string; defaultEmail?: string }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [done, setDone] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      request('/auth/password/request', {
        method: 'POST',
        body: JSON.stringify({
          email,
          ...(token ? { turnstileToken: token } : {}),
        }),
      }),
    onSuccess: () => setSent(true),
  });
  const reset = useMutation({
    mutationFn: () =>
      request('/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({ email, code, password }),
      }),
    onSuccess: () => setDone(true),
  });
  return (
    <>
      <button
        type="button"
        className="signin-link"
        onClick={() => {
          // Carry the address from step one so recovery starts where the user already is.
          if (!sent && defaultEmail) setEmail(defaultEmail);
          setOpen(true);
        }}
      >
        忘记密码？
      </button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="重置密码"
        description={
          done
            ? '密码已更新，请使用新密码登录。'
            : sent
              ? '如果该邮箱已注册，我们会发送验证码。请输入邮件中的验证码与新密码。'
              : '输入你的账户邮箱，我们会发送重置验证码。'
        }
      >
        {done ? (
          <Button variant="primary" onClick={() => setOpen(false)}>
            返回登录
          </Button>
        ) : (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (sent) reset.mutate();
              else send.mutate();
            }}
          >
            <Field label="邮箱">
              <Input
                type="email"
                required
                autoComplete="email"
                value={email}
                disabled={sent}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            {sent ? (
              <>
                <Field label="邮箱验证码">
                  <Input
                    required
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                </Field>
                <Field label="新密码" hint="至少 12 个字符。">
                  <Input
                    type="password"
                    required
                    minLength={12}
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>
              </>
            ) : siteKey ? (
              <Challenge siteKey={siteKey} onToken={setToken} />
            ) : null}
            <ErrorNotice error={send.error || reset.error} />
            <div className="modal-actions">
              <Button
                onClick={() => {
                  if (sent) setSent(false);
                  else setOpen(false);
                }}
              >
                {sent ? '更换邮箱' : '取消'}
              </Button>
              <Button
                variant="primary"
                type="submit"
                busy={send.isPending || reset.isPending}
                disabled={!sent && Boolean(siteKey && !token)}
              >
                {sent ? '更新密码' : '发送验证码'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Step = 'email' | 'secret';
type Notice = { tone: 'warn' | 'success' | 'info'; text: string };

function readNotice(register: boolean): Notice | null {
  if (register) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('session') === 'expired')
    return {
      tone: 'warn',
      text: '登录已过期或设备授权已撤销，请重新登录。尚未同步的备注草稿仍保留在当前浏览器。',
    };
  if (params.get('account') === 'deleting')
    return { tone: 'success', text: '注销已受理，已退出登录，资料将永久删除。' };
  if (params.get('social') === 'cancelled')
    return { tone: 'info', text: '已取消第三方登录，你可以重新选择登录方式。' };
  return null;
}

function NoticeBanner({ notice }: { notice: Notice }) {
  const Icon =
    notice.tone === 'success' ? CheckCircle : notice.tone === 'warn' ? WarningCircle : Info;
  return (
    <p className="signin-notice" data-tone={notice.tone} role="status">
      <Icon size={16} weight="fill" aria-hidden="true" />
      <span>{notice.text}</span>
    </p>
  );
}

export function AuthPage(props: { register?: boolean; embedded?: boolean }) {
  return isDesktop() ? <DesktopAuthPage /> : <BrowserAuthPage {...props} />;
}
function BrowserAuthPage({
  register = false,
  embedded = false,
}: {
  register?: boolean;
  embedded?: boolean;
}) {
  const session = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('email');
  const [direction, setDirection] = useState<'none' | 'forward' | 'back'>('none');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [challenge, setChallenge] = useState('');
  const [emailError, setEmailError] = useState('');
  const [secretError, setSecretError] = useState('');
  const [notice] = useState(() => readNotice(register));
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const passwordId = useId();
  const passwordErrorId = `${passwordId}-error`;
  const passwordHintId = `${passwordId}-hint`;
  const mutation = useMutation({
    mutationFn: () =>
      register
        ? api.register({
            email,
            password,
            name: name.trim(),
            ...(challenge ? { turnstileToken: challenge } : {}),
          })
        : api.login({ email, password }),
    onSuccess: async () => {
      client.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'session',
      });
      await client.invalidateQueries({ queryKey: sessionKey });
      if (!embedded) {
        const resume = sessionStorage.getItem('kiipu.auth.resume');
        sessionStorage.removeItem('kiipu.auth.resume');
        if (resume?.startsWith('/extension/authorize?')) window.location.assign(resume);
        else void navigate({ to: '/' });
      }
    },
    onError: () => {
      // Let the user retype immediately instead of hunting for the field.
      requestAnimationFrame(() => {
        passwordRef.current?.focus();
        passwordRef.current?.select();
      });
    },
  });
  const registrationOpen = session.data?.registrationOpen !== false;
  const closed = register && !registrationOpen;

  useEffect(() => {
    if (step === 'secret') (register ? nameRef : passwordRef).current?.focus();
    else if (direction === 'back') {
      emailRef.current?.focus();
      emailRef.current?.select();
    } else emailRef.current?.focus();
  }, [step, direction, register]);

  function submitEmail(event: FormEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setEmailError(value ? '请输入有效的邮箱地址。' : '请输入邮箱地址。');
      emailRef.current?.focus();
      return;
    }
    setEmail(value);
    setEmailError('');
    setDirection('forward');
    setStep('secret');
  }

  function submitSecret(event: FormEvent) {
    event.preventDefault();
    if (register && !name.trim()) {
      setSecretError('');
      nameRef.current?.focus();
      return;
    }
    if (!password) {
      setSecretError('请输入密码。');
      passwordRef.current?.focus();
      return;
    }
    if (register && password.length < 12) {
      setSecretError('密码至少需要 12 个字符。');
      passwordRef.current?.focus();
      return;
    }
    setSecretError('');
    mutation.mutate();
  }

  function changeEmail() {
    mutation.reset();
    setPassword('');
    setSecretError('');
    setChallenge('');
    setDirection('back');
    setStep('email');
  }

  const passwordInvalid = Boolean(secretError || mutation.error);
  const title = register ? '留住每一次灵感' : '欢迎回到 Kiipu';
  const subtitle = register ? '从一篇文章开始，建立你的内容库。' : '值得保存的内容，随时回来阅读。';

  return (
    <main className="signin">
      <header className="signin-top">
        <Link to="/" search={{}} className="signin-home" aria-label="Kiipu 首页">
          <Brand />
        </Link>
        {register ? (
          <p className="signin-switch">
            <span>已有账户？</span>
            <Link to="/login">{t('登录')}</Link>
          </p>
        ) : registrationOpen ? (
          <p className="signin-switch">
            <span>还没有账户？</span>
            <Link to="/register">{t('注册')}</Link>
          </p>
        ) : null}
      </header>

      <section className="signin-body" aria-labelledby="signin-title">
        <div className="signin-column">
          <div className="signin-mark" aria-hidden="true">
            <Brand compact />
          </div>
          <h1 id="signin-title">{title}</h1>
          {step === 'email' ? (
            <p className="signin-subtitle">{subtitle}</p>
          ) : (
            <EmailChip email={email} onChange={changeEmail} disabled={mutation.isPending} />
          )}

          {notice && step === 'email' ? <NoticeBanner notice={notice} /> : null}

          {step === 'email' ? (
            <form
              key="email"
              className="signin-form"
              data-direction={direction}
              noValidate
              onSubmit={submitEmail}
            >
              <Field label={t('邮箱')} error={emailError}>
                <Input
                  ref={emailRef}
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  aria-invalid={Boolean(emailError) || undefined}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) setEmailError('');
                  }}
                  placeholder="you@example.com"
                />
              </Field>
              <ErrorNotice error={session.error || (closed ? '当前暂未开放注册。' : null)} />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="signin-submit"
                disabled={session.isPending || closed}
              >
                继续
                <ArrowRight size={16} aria-hidden="true" />
              </Button>
            </form>
          ) : (
            <form
              key="secret"
              className="signin-form"
              data-direction={direction}
              noValidate
              onSubmit={submitSecret}
            >
              {/* Lets password managers pair the saved secret with the address from step one. */}
              <input
                className="signin-username"
                type="email"
                name="username"
                autoComplete="username"
                value={email}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
              {register ? (
                <Field label={t('名称')}>
                  <Input
                    ref={nameRef}
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={80}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="你希望被如何称呼"
                  />
                </Field>
              ) : null}
              <div className="signin-field">
                <div className="signin-label-row">
                  <label htmlFor={passwordId}>{t('密码')}</label>
                  {!register ? (
                    <PasswordRecovery
                      siteKey={session.data?.turnstileSiteKey}
                      defaultEmail={email}
                    />
                  ) : null}
                </div>
                <PasswordInput
                  ref={passwordRef}
                  id={passwordId}
                  name="password"
                  autoComplete={register ? 'new-password' : 'current-password'}
                  required
                  minLength={register ? 12 : undefined}
                  value={password}
                  aria-invalid={passwordInvalid || undefined}
                  aria-describedby={
                    [secretError ? passwordErrorId : '', register ? passwordHintId : '']
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (secretError) setSecretError('');
                    if (mutation.error) mutation.reset();
                  }}
                />
                {secretError ? (
                  <p className="signin-error" id={passwordErrorId}>
                    {secretError}
                  </p>
                ) : null}
                {register ? (
                  <p className="signin-hint" id={passwordHintId}>
                    至少 12 个字符，建议使用密码管理器生成。
                  </p>
                ) : null}
              </div>
              {register && session.data?.turnstileSiteKey ? (
                <Challenge siteKey={session.data.turnstileSiteKey} onToken={setChallenge} />
              ) : null}
              <ErrorNotice
                error={mutation.error || session.error || (closed ? '当前暂未开放注册。' : null)}
              />
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="signin-submit"
                busy={mutation.isPending}
                disabled={
                  session.isPending ||
                  closed ||
                  Boolean(register && session.data?.turnstileSiteKey && !challenge)
                }
              >
                {register ? '创建账户' : t('登录')}
              </Button>
            </form>
          )}

          {step === 'email' && !closed ? <SocialSignInButtons divider /> : null}

          <p className="signin-legal">
            继续即表示你同意 Kiipu 的<Link to="/terms">服务条款</Link>与
            <Link to="/privacy">隐私政策</Link>。
          </p>
        </div>
      </section>

      <footer className="signin-footer">
        <Link to="/help">使用帮助</Link>
        <span aria-hidden="true">·</span>
        <Link to="/about">关于 Kiipu</Link>
      </footer>
    </main>
  );
}
